import { useMemo, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { PagedResult } from '@/api/types/abp';

export interface ServerTableRequest {
  skipCount: number;
  maxResultCount: number;
  sorting?: string;
  search?: string;
  draw?: number;
}

export interface SortState {
  key: string;
  dir: 'asc' | 'desc';
}

// Structural view of what useServerDataTable returns — the server-mode data source
// a table renders from. Lives here (not in a table component) so DataGrid/DataTable
// both consume the same contract.
export interface TableController<T> {
  rows: T[];
  totalCount: number;
  pageCount: number;
  pageIndex: number;
  pageSize: number;
  sort: SortState | null;
  search: string;
  columnFilters: Record<string, string>;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  setPageIndex: (n: number) => void;
  setPageSize: (n: number) => void;
  toggleSort: (key: string) => void;
  applySearch: (v: string) => void;
  setColumnFilter: (key: string, value: string) => void;
  refetch: () => void;
}

interface Options<T, Extra extends object> {
  queryKey: readonly unknown[];
  fetcher: (req: ServerTableRequest & Extra) => Promise<PagedResult<T>>;
  pageSize?: number;
  // Feature-specific filters (e.g. the group filter) merged into every request.
  extraParams?: Extra;
  enabled?: boolean;
  // Default server sort applied on first load (e.g. { key: 'triggeredAt', dir: 'desc' }).
  initialSort?: SortState;
}

// Server-side paging/sorting/search state + data fetching. The single source of
// truth the DataTable renders from. Mirrors the legacy skipCount/maxResultCount/
// sorting contract so the existing app-service endpoints work unchanged.
export function useServerDataTable<T, Extra extends object = Record<string, never>>({
  queryKey,
  fetcher,
  pageSize = 10,
  extraParams,
  enabled = true,
  initialSort,
}: Options<T, Extra>) {
  const [pageIndex, setPageIndex] = useState(0);
  const [size, setSize] = useState(pageSize);
  const [sort, setSort] = useState<SortState | null>(initialSort ?? null);
  const [search, setSearch] = useState('');
  // Per-column filters (the legacy finkit-dt-search-row), sent as named params.
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const drawRef = useRef(0);

  // Serialise the by-value inputs so a caller that passes a fresh object each render
  // (new reference, same value) — e.g. `extraParams={{ id }}` without useMemo — does
  // NOT retrigger the query. Keying on the volatile `request` (which carries an
  // ever-incrementing `draw`) previously caused an infinite refetch loop in that case.
  const sorting = sort ? `${sort.key} ${sort.dir}` : undefined;
  const searchParam = search.trim() || undefined;
  const columnKey = JSON.stringify(columnFilters);
  const extraKey = JSON.stringify(extraParams ?? {});

  const request = useMemo<ServerTableRequest & Extra>(() => {
    drawRef.current += 1;
    // The CONTROLLER owns paging/sort/search/draw, so spread extraParams + column
    // filters FIRST and the paging fields LAST — otherwise a feature filter that
    // happens to carry skipCount/maxResultCount (e.g. Client Profitability's 50000)
    // would override the real page size and fetch everything.
    return {
      ...(extraParams ?? ({} as Extra)),
      ...columnFilters,
      skipCount: pageIndex * size,
      maxResultCount: size,
      sorting,
      search: searchParam,
      draw: drawRef.current,
    } as ServerTableRequest & Extra;
    // Depend on the serialised keys, not the raw objects; drawRef is derived, not a trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, size, sorting, searchParam, columnKey, extraKey]);

  const query = useQuery({
    // Key on the stable param values (NOT the request object with its volatile draw),
    // so identical params reuse the cache instead of refetching.
    queryKey: [...queryKey, pageIndex, size, sorting, searchParam, columnKey, extraKey],
    queryFn: () => fetcher(request),
    placeholderData: keepPreviousData,
    enabled,
  });

  const totalCount = query.data?.totalCount ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / size));

  function toggleSort(key: string) {
    setPageIndex(0);
    setSort((prev) => {
      if (prev?.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  }

  function applySearch(value: string) {
    setPageIndex(0);
    setSearch(value);
  }

  function setColumnFilter(key: string, value: string) {
    setPageIndex(0);
    setColumnFilters((prev) => {
      const next = { ...prev };
      if (value.trim()) next[key] = value.trim();
      else delete next[key];
      return next;
    });
  }

  return {
    rows: query.data?.items ?? [],
    totalCount,
    pageCount,
    pageIndex,
    pageSize: size,
    sort,
    search,
    columnFilters,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    setPageIndex,
    setPageSize: (n: number) => {
      setPageIndex(0);
      setSize(n);
    },
    toggleSort,
    applySearch,
    setColumnFilter,
  };
}

export type ServerDataTable<T> = ReturnType<typeof useServerDataTable<T>>;
